/**
 * The parts of the quote request that both sides need.
 *
 * Kept out of `quote-request.server.ts` because the form component reads the
 * action result, and importing the server module to get a type dragged Prisma,
 * Resend and the throttle into the client graph — which fails the build with
 * "Server-only module referenced by client" rather than shipping something
 * broken, but only at build time.
 *
 * Same split as rating-concentration: the pure contract lives here, the work
 * lives beside the database.
 */

export const QUOTE_REQUEST_INTENT = 'quote-request'

/**
 * Every result carries `form` so a shared component can recognise its own
 * action data.
 *
 * The form renders on three routes, so it cannot type useActionData against a
 * single `typeof action`. Without a discriminator, a second intent added to any
 * of those routes would return a differently shaped object that a bare cast
 * would happily render as a quote result.
 *
 * `status` travels with the failure because not every rejection is the same
 * kind: a throttled submission is 429 so abuse can be alerted on separately
 * from ordinary validation noise.
 */
export type QuoteRequestResult =
	| { form: typeof QUOTE_REQUEST_INTENT; ok: true }
	| {
			form: typeof QUOTE_REQUEST_INTENT
			ok: false
			error: string
			status: number
	  }

export function isQuoteRequestResult(
	value: unknown,
): value is QuoteRequestResult {
	return (
		typeof value === 'object' &&
		value !== null &&
		(value as { form?: unknown }).form === QUOTE_REQUEST_INTENT
	)
}
